import ApiService from './api.service';
import AuthService from './auth.service';
import type { ListRecordsResponse, SingleRecordResponse } from './pipelineNode.service';

export type { ListRecordsResponse };

export interface JobStatusResponse {
  job_status: 'queued' | 'started' | 'deferred' | 'scheduled' | 'finished' | 'failed';
  job_result?: any;
  error_message?: string;
  progress?: any;
  meta?: any;
}

export type MetricType = 'BASIC' | 'CALCULATED' | 'COHORT';

export type MeasureAggregation =
  | 'COUNT_DISTINCT'
  | 'PICK_ONE'
  | 'MIN'
  | 'MAX'
  | 'AVG'
  | 'SUM'
  | 'MEDIAN'
  | 'PERCENTILE_CONT'
  | 'PERCENTILE_DISC'
  | 'CONCAT';

export interface DataWhitelistEntry {
  pipeline_node_id: string;
  field_id: string;
  value?: string | string[];
  comparator: string;
}

export interface DataWhitelist {
  entries: DataWhitelistEntry[];
  logic_gate: string;
  custom_sql?: string;
}

export interface MeasureJoinTreeNode {
  node_id: string;
  relationship_id?: string;
  downstream: MeasureJoinTreeNode[];
}

export interface DimensionJoinTree {
  pipeline_node_id: string;
  join_tree: MeasureJoinTreeNode | null;
  ignore: boolean;
  label?: string;
  custom_path?: MeasureJoinTreeNode;
}

export interface JoinPathOption {
  pipeline_node_id: string;
  measure_id: string;
  possible_paths: MeasureJoinTreeNode[];
}

export type CohortAggregation =
  | 'COUNT_NON_ZERO_COHORT_CONDITION'
  | 'COUNT_ZERO_COHORT_CONDITION'
  | 'COUNT_REACTIVATED_CONDITION'
  | 'COUNT_RETURNING_COHORT_CONDITION'
  | 'COUNT_FIRST_ACTIVITY_CONDITION'
  | 'SUM_COHORT_CONDITION';

export type CohortLookbackUnit = 'DAY' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR';

/**
 * How far back a retained/churned cohort metric looks for prior activity: a
 * member was previously active if they had activity in
 * [period start - count × unit, period start).
 */
export interface CohortLookback {
  count: number;
  unit: CohortLookbackUnit;
}

/** Cohort aggregations that compare against a lookback window. */
export const LOOKBACK_COHORT_AGGREGATIONS: string[] = ['COUNT_NON_ZERO_COHORT_CONDITION', 'COUNT_ZERO_COHORT_CONDITION'];

interface LookbackCandidate {
  id: string;
  metric_type?: string;
  aggregator?: string;
  parent_measure_ids?: string[];
}

/**
 * The retained/churned cohort metrics a metric is, or is calculated from — the
 * ones a cohort lookback affects. Mirrors Measure.lookback_cohort_measures in
 * src/models/reporting.py, so a churn rate built on Churned Players counts too.
 */
export function lookbackCohortMeasures<T extends LookbackCandidate>(
  measure: T,
  measuresById: Map<string, T>,
  seen: Set<string> = new Set()
): T[] {
  if (measure.metric_type === 'COHORT' && LOOKBACK_COHORT_AGGREGATIONS.includes(measure.aggregator || '')) {
    return [measure];
  }
  if (measure.metric_type !== 'CALCULATED' || seen.has(measure.id)) return [];
  seen.add(measure.id);
  return (measure.parent_measure_ids || []).flatMap(pid => {
    const parent = measuresById.get(pid);
    return parent ? lookbackCohortMeasures(parent, measuresById, seen) : [];
  });
}

export interface Measure {
  id: string;
  name: string;
  column_name?: string;
  pipeline_node_id?: string;
  field_id?: string;
  description?: string;
  metric_type: MetricType;
  formula?: string;
  is_calculation?: boolean;
  parent_measure_ids?: string[];
  aggregator?: MeasureAggregation;
  percentile?: number;
  formatter?: string;
  data_whitelist?: DataWhitelist | null;
  dimension_join_trees?: DimensionJoinTree[];
  created_at?: string;
  updated_at?: string;
  // Cohort fields - reference existing measure and dimension
  activity_measure_id?: string;
  cohort_dimension_id?: string;
  /** Default lookback for retained/churned cohort metrics; analyses can override it. */
  cohort_lookback?: CohortLookback | null;
  taxonomic_id?: string;
  managed_by_template_id?: string;
  allow_approximate_distinct?: boolean;
  dimension_strict_mode?: boolean;
}

export class MeasureService {
  async find(options?: {
    page?: number;
    pageSize?: number;
    search?: string;
    sort?: string;
    filters?: Record<string, any>;
  }): Promise<ListRecordsResponse<Measure>> {
    const {
      page = 1,
      pageSize = 50,
      search,
      sort,
      filters = {},
    } = options || {};

    const queryParams: Record<string, any> = {
      page,
      limit: pageSize,
      ...filters,
    };

    if (search && search.trim()) {
      queryParams.search = search.trim();
    }
    if (sort) {
      queryParams.sort = sort;
    }

    const result = await ApiService.getInstance().request(
      'GET',
      '/measures',
      queryParams
    ) as ListRecordsResponse<Measure>;

    return result;
  }

  async findById(id: string): Promise<Measure> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/measures/${id}`
    ) as SingleRecordResponse<Measure>;
    return result.record;
  }

  async save(measure: Partial<Measure>): Promise<Measure> {
    const result = await ApiService.getInstance().request(
      'PATCH',
      `/measures/${measure.id}`,
      measure
    ) as SingleRecordResponse<Measure>;
    return result.record;
  }

  async create(measure: Partial<Measure>): Promise<Measure> {
    const result = await ApiService.getInstance().request(
      'POST',
      '/measures',
      measure
    ) as SingleRecordResponse<Measure>;
    return result.record;
  }

  async delete(id: string): Promise<void> {
    await ApiService.getInstance().request(
      'DELETE',
      `/measures/${id}`
    );
  }

  /**
   * Get the job ID for the process_measures background job for the current tenant.
   */
  getProcessMeasuresJobId(): string | null {
    const authState = AuthService.getInstance().getAuthState();
    if (!authState?.tenant_id) {
      return null;
    }
    return `process_measures_${authState.tenant_id}`;
  }

  /**
   * Check if the process_measures job is currently running.
   * Returns the job status if the job exists, null if not found.
   */
  async getProcessMeasuresJobStatus(): Promise<JobStatusResponse | null> {
    const jobId = this.getProcessMeasuresJobId();
    if (!jobId) {
      return null;
    }

    try {
      const result = await ApiService.getInstance().request(
        'GET',
        `/get-job-status/${jobId}`
      ) as JobStatusResponse;
      return result;
    } catch (err: any) {
      // 404 means job doesn't exist (not running)
      if (err?.code === 404) {
        return null;
      }
      throw err;
    }
  }

  /**
   * Check if the process_measures job is currently in a running state.
   */
  async isProcessMeasuresRunning(): Promise<boolean> {
    const status = await this.getProcessMeasuresJobStatus();
    if (!status) {
      return false;
    }
    return ['queued', 'started', 'deferred', 'scheduled'].includes(status.job_status);
  }

  /**
   * Manually trigger recalculation of measure join paths.
   */
  async recalculateJoinPaths(): Promise<{ job_id: string; status: string }> {
    const result = await ApiService.getInstance().request(
      'POST',
      '/measures/recalculate-join-paths'
    ) as { job_id: string; status: string };
    return result;
  }
}

export const measureService = new MeasureService();
